import type { DeliveryFeeSource, SettlementStatus } from '@/types';

/**
 * Demo finance metadata (FB07).
 *
 * Only the finance-only extras live here: the delivery fee transferred to the
 * laundry, its source, the recorded customer debt, and whether the amount was
 * already covered by a transferred settlement. The recognised amount itself is
 * NEVER stored here — the service reads every completed order from the shared
 * orders store, so the finance figures and the homepage revenue can never
 * contradict each other. Replaced by the finance API later.
 */
export interface FinanceMeta {
  /** Delivery fee recorded for the laundry (0 when executed by an independent driver). */
  deliveryFeeToLaundry: number;
  deliveryFeeSource: DeliveryFeeSource;
  /** Customer debt recorded by the system; never reduces the laundry net payable. */
  customerDebt: number;
  /** True once the amount was covered by a transferred settlement. */
  settled: boolean;
}

/**
 * Finance extras for the completed orders of the demo dataset.
 * LB-1024 + LB-1023 were already covered by the transferred settlement (274.25),
 * the rest compose the open balance.
 */
export const mockFinanceMeta: Record<string, FinanceMeta> = {
  'LB-1033': { deliveryFeeToLaundry: 18, deliveryFeeSource: 'sub_driver', customerDebt: 0, settled: false },
  'LB-1032': { deliveryFeeToLaundry: 0, deliveryFeeSource: 'none', customerDebt: 0, settled: false },
  'LB-1031': { deliveryFeeToLaundry: 18, deliveryFeeSource: 'sub_driver', customerDebt: 30, settled: false },
  'LB-1030': { deliveryFeeToLaundry: 15, deliveryFeeSource: 'sub_driver', customerDebt: 0, settled: false },
  'LB-1029': { deliveryFeeToLaundry: 0, deliveryFeeSource: 'independent', customerDebt: 0, settled: false },
  'LB-1024': { deliveryFeeToLaundry: 0, deliveryFeeSource: 'none', customerDebt: 0, settled: true },
  'LB-1023': { deliveryFeeToLaundry: 15, deliveryFeeSource: 'sub_driver', customerDebt: 0, settled: true },
};

export interface SettlementRecord {
  id: string;
  requestedDaysAgo: number;
  processedDaysAgo: number | null;
  amount: number;
  status: SettlementStatus;
  rejectionReason: string | null;
  /** Transfer proof image; null until an administration transfer has a receipt. */
  transferReceiptUrl: string | null;
}

/** Settlement history: one processing, one transferred, one rejected with a reason. */
export const mockSettlements: SettlementRecord[] = [
  {
    id: 'STL-5003',
    requestedDaysAgo: 1,
    processedDaysAgo: null,
    amount: 200,
    status: 'processing',
    rejectionReason: null,
    transferReceiptUrl: null,
  },
  {
    id: 'STL-5002',
    requestedDaysAgo: 5,
    processedDaysAgo: 3,
    amount: 274.25,
    status: 'transferred',
    rejectionReason: null,
    transferReceiptUrl:
      'https://readdy.ai/api/search-image?query=Mobile%20banking%20app%20money%20transfer%20success%20screen%20with%20green%20confirmation%20check%2C%20clean%20minimal%20interface%2C%20soft%20neutral%20background%2C%20flat%20design%2C%20high%20detail&width=800&height=1100&seq=settlement-transfer-receipt-5002&orientation=portrait',
  },
  {
    id: 'STL-5001',
    requestedDaysAgo: 9,
    processedDaysAgo: 7,
    amount: 300,
    status: 'rejected',
    rejectionReason: 'بيانات الحساب البنكي المسجل غير مكتملة، يرجى تحديثها ثم إعادة تقديم الطلب.',
    transferReceiptUrl: null,
  },
];